import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Check } from 'lucide-react';
import PropTypes from 'prop-types';
import { API_URLS } from '../../../constants/api';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

const ChargesDropdown = ({
  isOpen,
  onClose,
  selectedCharges = [],
  onChargesChange,
  chargeLogic = 'OR',
  onChargeLogicChange,
  buttonRef
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [commonCharges, setCommonCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchInputRef = useRef(null);

  // Use smart positioning hook - let it handle mobile detection internally
  const { dropdownRef, position } = useDropdownPosition(isOpen, 400, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  // Fetch common charges on mount
  useEffect(() => {
    const fetchCommonCharges = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URLS.SEARCH_CHARGES}?searchQuery=&includeCommon=true`
        );
        if (response.ok) {
          const data = await response.json();
          const topCharges = data
            .filter((charge) => !charge.code.includes('Various'))
            .slice(0, 8);
          setCommonCharges(topCharges);
        }
      } catch (error) {
        console.error('Error fetching common charges:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommonCharges();
  }, []);

  // Fetch suggestions based on search term
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_URLS.SEARCH_CHARGES}?searchQuery=${encodeURIComponent(searchTerm)}&includeCommon=true`
        );
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data);
        }
      } catch (error) {
        console.error('Error fetching charge suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle charge selection
  const handleChargeSelect = (charge) => {
    const isSelected = selectedCharges.find((c) => c.code === charge.code);
    if (isSelected) {
      onChargesChange(selectedCharges.filter((c) => c.code !== charge.code));
    } else {
      onChargesChange([...selectedCharges, charge]);
    }
    setSearchTerm('');
    setSuggestions([]);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const allOptions = [...commonCharges, ...suggestions];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < allOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > -1 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < allOptions.length) {
          handleChargeSelect(allOptions[focusedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onChargesChange([]);
  };

  if (!isOpen) return null;

  const hasSelection = selectedCharges.length > 0;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-72 sm:w-96'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '260px' // Even smaller minimum width for mobile
              }
        }
      >
        <div className="p-1.5 sm:p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5 sm:mb-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-900">
              Select Charges
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-2 sm:mb-4">
            <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setFocusedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search by code or description..."
              className="w-full pl-6 sm:pl-10 pr-2 sm:pr-3 py-1 sm:py-2 border border-gray-300 rounded-md text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>

          {/* Selected Charges */}
          {hasSelection && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Selected ({selectedCharges.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedCharges.map((charge) => (
                  <span
                    key={charge.code}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {charge.description}
                    <button
                      onClick={() => handleChargeSelect(charge)}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {/* Charge Logic Selector - only show when more than 1 charge */}
              {selectedCharges.length > 1 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    Match charges with:
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onChargeLogicChange('AND')}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        chargeLogic === 'AND'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      AND
                    </button>
                    <button
                      onClick={() => onChargeLogicChange('OR')}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        chargeLogic === 'OR'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      OR
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {chargeLogic === 'AND'
                      ? 'Cases must have ALL selected charges'
                      : 'Cases must have ANY of the selected charges'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Common Charges */}
          {!searchTerm && (
            <div className="mb-2 sm:mb-4">
              <div className="text-xs font-medium text-gray-700 mb-1 sm:mb-2">
                Common Charges
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2">
                {commonCharges.map((charge, index) => {
                  const isSelected = selectedCharges.find(
                    (c) => c.code === charge.code
                  );
                  const isFocused = focusedIndex === index;
                  return (
                    <button
                      key={charge.code}
                      onClick={() => handleChargeSelect(charge)}
                      className={`p-1.5 sm:p-2 text-left text-xs border rounded-md transition-colors ${
                        isSelected
                          ? 'bg-blue-50 border-blue-200 text-blue-900'
                          : isFocused
                            ? 'bg-gray-50 border-gray-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{charge.code}</span>
                        {isSelected && (
                          <Check className="w-3 h-3 text-blue-600" />
                        )}
                      </div>
                      <div className="text-gray-500 mt-1 truncate">
                        {charge.description?.substring(0, 30)}...
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search Results */}
          {searchTerm && suggestions.length > 0 && (
            <div className="mb-2 sm:mb-4">
              <div className="text-xs font-medium text-gray-700 mb-1 sm:mb-2">
                Search Results
              </div>
              <div className="max-h-32 sm:max-h-48 overflow-y-auto">
                {suggestions.map((charge, index) => {
                  const isSelected = selectedCharges.find(
                    (c) => c.code === charge.code
                  );
                  const isFocused =
                    focusedIndex === commonCharges.length + index;
                  return (
                    <button
                      key={charge.code}
                      onClick={() => handleChargeSelect(charge)}
                      className={`w-full p-1.5 sm:p-2 text-left text-xs border-b border-gray-100 last:border-b-0 transition-colors ${
                        isSelected
                          ? 'bg-blue-50 text-blue-900'
                          : isFocused
                            ? 'bg-gray-50'
                            : 'hover:bg-gray-50'
                      }`}
                      onMouseEnter={() =>
                        setFocusedIndex(commonCharges.length + index)
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{charge.code}</div>
                          <div className="text-gray-500 truncate">
                            {charge.description}
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="w-3 h-3 text-blue-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchTerm && suggestions.length === 0 && !loading && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No charges found for &quot;{searchTerm}&quot;
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-200">
            <button
              onClick={handleClearAll}
              className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
              disabled={!hasSelection}
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

ChargesDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCharges: PropTypes.arrayOf(PropTypes.object),
  onChargesChange: PropTypes.func.isRequired,
  chargeLogic: PropTypes.oneOf(['AND', 'OR']),
  onChargeLogicChange: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default ChargesDropdown;
